import type { ChangeEvent } from 'react'
import { useCallback, useEffect, useState } from 'react'

import CheckIcon from '@/assets/icons/icon-check.svg'
import SearchIcon from '@/assets/icons/icon-search.svg'
import NotFoundImage from '@/assets/images/not-found.svg'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import type { Creator } from '@/modules/creators/types'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'
import { classNames } from '@/utils/helpers.ts'

export interface ConnectCreatorDialogProps {
  open: boolean
  onClose: () => void
  title: string
  description: string | JSX.Element
  creators: Creator[]
  onSubmit: (selectedCreator: Creator[]) => Promise<void>
  submitButtonLabel: string
  isSubmitButtonLoading: boolean
  noCreatorMessage: string
  multiple?: boolean
}

export const CreatorsListDialog = (props: ConnectCreatorDialogProps) => {
  const {
    open,
    onClose,
    title,
    description,
    creators,
    onSubmit,
    submitButtonLabel,
    isSubmitButtonLoading,
    noCreatorMessage,
    multiple = false,
  } = props

  const [filteredCreators, setFilteredCreators] = useState<Creator[]>()
  const [selectedCreators, setSelectedCreator] = useState<Creator[]>([])

  useEffect(() => {
    setSelectedCreator([])
  }, [open])

  const handleSubmit = () => {
    if (!selectedCreators.length) return

    onSubmit(selectedCreators).finally(() => {
      setFilteredCreators(creators)
      setSelectedCreator([])
      onClose()
    })
  }

  const handleSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const search = event.target.value.toLowerCase()
      const result = creators.filter((creator) =>
        creator.name.toLowerCase().includes(search),
      )

      if (result) {
        setFilteredCreators(result)
      }
    },
    [creators],
  )

  const handleClick = useCallback(
    (creator: Creator) => {
      if (multiple) {
        if (selectedCreators.includes(creator)) {
          setSelectedCreator((prevValue) =>
            prevValue.filter((item) => item.id !== creator.id),
          )
        } else {
          setSelectedCreator((prevValue) => prevValue.concat(creator))
        }
      } else {
        setSelectedCreator([creator])
      }
    },
    [selectedCreators, multiple],
  )

  if (creators.length === 0) {
    return (
      <Dialog
        show={open}
        onClose={onClose}
      >
        <Dialog.Header className="flex flex-col items-center">
          <NotFoundImage />
          <Dialog.Title>No available Creators</Dialog.Title>
          <Dialog.Description className="!mt-6 block text-center">
            {noCreatorMessage}
          </Dialog.Description>
        </Dialog.Header>
      </Dialog>
    )
  }

  return (
    <Dialog
      show={open}
      onClose={onClose}
    >
      <Dialog.Header>
        <Dialog.Title className="text-center">{title}</Dialog.Title>
        <Dialog.Description className="text-center">
          {description}
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Description>
        <TextField
          placeholder="Search Creator"
          className="mb-4 mt-2 resize-none"
          prefix={<SearchIcon className="text-title dark:text-secondary-300" />}
          onChange={handleSearch}
        />
        <div className="max-h-96 divide-y divide-border overflow-auto">
          {(filteredCreators || creators).map((creator) => {
            const isSelected = selectedCreators.includes(creator)

            return (
              <Button
                key={creator.id}
                variant="ghost"
                onClick={() => handleClick(creator)}
                className={classNames(
                  'flex h-max w-full justify-start gap-3 border-none px-8 py-3 hover:bg-primary-50 dark:hover:bg-primary-600/20',
                  {
                    'bg-primary-50 dark:bg-primary-600/20': isSelected,
                  },
                )}
              >
                <div className="flex w-full items-center gap-3">
                  <CreatorAvatar
                    size={40}
                    avatarSrc={creator.avatar.croppedUrl}
                    name={creator.name}
                  />
                  <div className="font-medium text-title">{creator.name}</div>
                </div>
                {isSelected && <CheckIcon />}
              </Button>
            )
          })}
        </div>
      </Dialog.Description>
      <Dialog.ClosableFooter onClose={onClose}>
        <Button
          onClick={handleSubmit}
          disabled={!selectedCreators.length}
          isLoading={isSubmitButtonLoading}
        >
          {submitButtonLabel}
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
